import { CaretDown } from "@phosphor-icons/react";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BOOKING_HORIZON_OPTIONS } from "@/lib/booking-horizon";
import { WeeklyAvailabilityEditor } from "../WeeklyAvailabilityEditor";
import type { BookingSettingsState, DayAvailability } from "../types";

interface Props {
  days: DayAvailability[];
  onDaysChange: (d: DayAvailability[]) => void;
  booking: BookingSettingsState;
  onBookingChange: (b: BookingSettingsState) => void;
}

export function AvailabilityStep({
  days,
  onDaysChange,
  booking,
  onBookingChange,
}: Props) {
  const setBooking = (patch: Partial<BookingSettingsState>) => onBookingChange({ ...booking, ...patch });

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Radni dani i radno vrijeme</Label>
        <WeeklyAvailabilityEditor days={days} onChange={onDaysChange} />
        <p className="text-xs text-muted-foreground">
          Uključi dane u koje radiš i upiši od kada do kada. Ikonom za kopiranje isto vrijeme
          primijeniš na sve uključene dane.
        </p>
      </div>

      {/* Ostala pravila rezervacije imaju razumne zadane vrijednosti; sklopljena su da
          prvi ekran traži samo radne dane i sate. */}
      <details className="group rounded-xl border border-border bg-card">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4 text-sm font-semibold [&::-webkit-details-marker]:hidden">
          <span>
            Dodatna pravila rezervacije
            <span className="block text-xs font-normal text-muted-foreground">
              Nije obavezno. Zadane vrijednosti odgovaraju većini terapeuta.
            </span>
          </span>
          <CaretDown className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
        </summary>
        <div className="grid gap-5 border-t border-border p-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Pauza između seansi</Label>
            <Select value={String(booking.bufferMinutes)} onValueChange={(v) => setBooking({ bufferMinutes: Number(v) })}>
              <SelectTrigger className="w-full bg-card"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[0, 10, 15, 30].map((m) => (
                  <SelectItem key={m} value={String(m)}>{m === 0 ? "Bez pauze" : `${m} min`}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Koliko unaprijed klijent mora rezervisati</Label>
            <Select value={String(booking.minBookingLeadDays)} onValueChange={(v) => setBooking({ minBookingLeadDays: Number(v) })}>
              <SelectTrigger className="w-full bg-card"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[0, 1, 2, 3, 7].map((d) => (
                  <SelectItem key={d} value={String(d)}>{d === 0 ? "Isti dan" : `Za ${d} ${d === 1 ? "dan" : "dana"}`}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Koliko daleko unaprijed se vide termini</Label>
            <Select
              value={booking.bookingHorizonDays === null ? "null" : String(booking.bookingHorizonDays)}
              onValueChange={(v) => setBooking({ bookingHorizonDays: v === "null" ? null : Number(v) })}
            >
              <SelectTrigger className="w-full bg-card"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BOOKING_HORIZON_OPTIONS.map((opt) => (
                  <SelectItem key={String(opt.value)} value={opt.value === null ? "null" : String(opt.value)}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center justify-between gap-4 rounded-lg border border-border p-3 sm:col-span-2">
            <span>
              <span className="block text-sm font-semibold">Primam nove klijente</span>
              <span className="block text-xs text-muted-foreground">Isključi da pauziraš nove rezervacije bez skrivanja profila.</span>
            </span>
            <Checkbox checked={booking.acceptingNewClients} onCheckedChange={(c) => setBooking({ acceptingNewClients: Boolean(c) })} />
          </label>
        </div>
      </details>
    </div>
  );
}
